import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router";
import { trackEvent } from "#app/utils/gtags.client.ts";
import { Button } from "../ui/button.tsx";
import { BackPreview } from "./back-preview.tsx";

type Props = {
  slug: string;
  printed: string;
  placement: "word" | "wotd";
};

/**
 * "Wear this word" (spec 02): the word on the jersey's nameplate, linking to /wear with it chosen.
 * The thumbnail only renders near the viewport (an SVG <image> can't lazy-load), in a box sized up front.
 */
export function WearItCard({ slug, printed, placement }: Props) {
  const headingId = useId();
  const box = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) {
      setShow(true);
      return;
    }
    const render = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setShow(true);
        render.disconnect();
      },
      { rootMargin: "300px" },
    );
    const view = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        trackEvent("wear_card_view", { word: slug, placement });
        view.disconnect();
      },
      { threshold: 0.5 },
    );
    render.observe(el);
    view.observe(el);
    return () => {
      render.disconnect();
      view.disconnect();
    };
  }, [slug, placement]);

  return (
    <section
      aria-labelledby={headingId}
      className="mt-6 rounded-lg border border-border bg-card p-4 sm:p-6"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
        <div
          ref={box}
          className="aspect-[4/3] w-full max-w-[240px] shrink-0 overflow-hidden rounded-md border border-border bg-muted sm:w-44"
        >
          {show ? <BackPreview crop="thumb" printed={printed} /> : null}
        </div>
        <div className="flex flex-col items-start gap-3">
          <h2 id={headingId} className="text-h5 leading-tight text-card-foreground">
            Wear {printed}
          </h2>
          <p className="font-sans text-body-sm text-muted-foreground">
            Independence 47 jersey · £55 · limited to{"\u00A0"}47
          </p>
          <Button asChild className="font-sans">
            <Link
              to={`/wear?word=${encodeURIComponent(slug)}`}
              aria-label={`Wear this word: ${printed} on the Independence 47 jersey`}
              onClick={() =>
                trackEvent("wear_card_click", { word: slug, placement })
              }
            >
              Wear this word
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
